// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, Input, OnInit } from '@angular/core';
import { downloadCSVFile, getFormattedDate } from '@lfx/core/helper';
import {
  EmployeeCertificatesReceivedStats,
  EmployeeCoursesEnrolledStats,
  Company
} from '@lfx/core/models';
import { CompanyService } from '@lfx/core/services';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'lfx-profile-snapshot-employee-training',
  templateUrl: './profile-snapshot-employee-training.component.html',
  styleUrls: ['./profile-snapshot-employee-training.component.scss'],
  standalone: false,
})
export class ProfileSnapshotEmployeeTrainingComponent implements OnInit {
  @Input() timeRange$: Observable<string>;
  @Input() userId: string;
  searchValue = '';
  _searchValue = new BehaviorSubject<string>('');
  searchValue$ = this._searchValue.asObservable();
  coursesCollectionSize = 0;
  coursesCurrentPage = 1;
  coursesPageSize = 10;
  _coursesPage = new BehaviorSubject<number>(1);
  coursesPage$ = this._coursesPage.asObservable();
  coursesData$: Observable<any[]> = of([]);
  coursesPageData$: Observable<any[]>;
  isCoursesLoading = true;
  certificatesCollectionSize = 0;
  certificatesCurrentPage = 1;
  certificatesPageSize = 10;
  _certificatesPage = new BehaviorSubject<number>(1);
  certificatesPage$ = this._certificatesPage.asObservable();
  certificatesData$: Observable<any[]> = of([]);
  certificatesPageData$: Observable<any[]>;
  isCertificatesLoading = true;
  myCompany$: Observable<Company>;
  activeTab = 'Courses';
  coursesColumns = [
    {
      name: 'Course Name',
      prop: 'courseName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Enrollment Date',
      prop: 'enrollmentTs',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  certificatesColumns = [
    {
      name: 'Course Name',
      prop: 'courseName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Issued Date',
      prop: 'issuedTs',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  constructor(private companyService: CompanyService) {
    this.myCompany$ = this.companyService.getMyCompany();
  }

  ngOnInit(): void {
    this.coursesData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._coursesPage.next(1);
          this.coursesCurrentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isCoursesLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeCoursesEnrolled(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange
          )
          .pipe(
            map(data => this.transformCoursesData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.coursesCollectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isCoursesLoading = false))
    );

    this.coursesPageData$ = combineLatest([
      this.coursesData$,
      this.coursesPage$,
    ]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.coursesPageSize;
        const endIndex = startIndex + this.coursesPageSize;

        return data.slice(startIndex, endIndex);
      })
    );
    this.certificatesData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._certificatesPage.next(1);
          this.certificatesCurrentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isCertificatesLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeCertificatesReceived(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange
          )
          .pipe(
            map(data => this.transformCertificatesData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.certificatesCollectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isCertificatesLoading = false))
    );

    this.certificatesPageData$ = combineLatest([
      this.certificatesData$,
      this.certificatesPage$,
    ]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.certificatesPageSize;
        const endIndex = startIndex + this.certificatesPageSize;

        return data.slice(startIndex, endIndex);
      })
    );
  }

  private transformCoursesData(data: EmployeeCoursesEnrolledStats[]): any[] {
    return data.map(item => ({
      courseName: this.formatText(item.courseName),
      enrollmentTs: this.formatDate(item.enrollmentTs),
    }));
  }

  private transformCertificatesData(
    data: EmployeeCertificatesReceivedStats[]
  ): any[] {
    return data.map(item => ({
      courseName: this.formatText(item.courseName),
      issuedTs: this.formatDate(item.issuedTs),
    }));
  }

  formatDate(dateString: string) {
    const formattedDate = getFormattedDate(dateString);

    return this.formatText(formattedDate);
  }

  formatText(text, subtext = null, url = null) {
    return {
      text,
      subtext,
      url,
    };
  }

  onCoursesPageChange(event: any) {
    this._coursesPage.next(event);
  }

  onCertificatesPageChange(event: any) {
    this._certificatesPage.next(event);
  }

  private filterData(data: any[], searchValue: string): any[] {
    return data.filter(i =>
      i.courseName.text.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  clearSearch() {
    this.searchValue = '';
    this.searchInputChange('');
  }

  searchInputChange(value: string): void {
    this._coursesPage.next(1);
    this.coursesCurrentPage = 1;
    this._certificatesPage.next(1);
    this.certificatesCurrentPage = 1;
    this._searchValue.next(value);
  }

  exportCoursesData() {
    const subscription = this.coursesData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Course Name': item.courseName.text,
          'Enrollmenet Date': item.enrollmentTs.text,
        })),
        'Employee Training Courses'
      );
      subscription.unsubscribe();
    });
  }

  exportCertificatesData() {
    const subscription = this.certificatesData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Course Name': item.courseName.text,
          'Issued Date': item.issuedTs.text,
        })),
        'Employee Training Certificates'
      );
      subscription.unsubscribe();
    });
  }

  // tabChange(newTabID, shouldRoute = true) {
  //   console.log('newTabID', newTabID, 'shouldRoute', shouldRoute);
  // }
}
